/**
 * RFL-142 — client-side screen-flow types + helpers. Mirrors
 * `packages/contracts/schemas/domain/screen-flow.ts`; the backend is the
 * authority (it re-validates on publish), but the editor runs the same
 * checks live so the operator sees issues before saving.
 */

export type ScreenSurface = 'pos' | 'sco' | 'attendant';

export type ScreenPort = { name: string; type: string };
export type ScreenEvent = { name: string; label?: string };

export type ScreenNodeDef = {
  id: string;
  label: string;
  isStart: boolean;
  isTerminal: boolean;
  insertable: boolean;
  events: ScreenEvent[];
  inputs: ScreenPort[];
  outputs: ScreenPort[];
};

export type ScreenManifest = {
  manifestVersion: string;
  surface: ScreenSurface;
  screens: ScreenNodeDef[];
};

export type JourneyNode = { nodeId: string; screenId: string; position: { x: number; y: number } };
export type JourneyEdge = { id: string; source: string; sourceEvent: string; target: string };
export type JourneyGraph = { manifestVersion: string; nodes: JourneyNode[]; edges: JourneyEdge[] };

export type GraphIssue = { code: string; message: string };

let counter = 0;
export function newId(prefix: string): string {
  counter += 1;
  return `${prefix}-${Date.now().toString(36)}-${counter}`;
}

export function screenById(manifest: ScreenManifest): Map<string, ScreenNodeDef> {
  return new Map(manifest.screens.map((s) => [s.id, s]));
}

export function eventLabel(screen: ScreenNodeDef | undefined, eventName: string): string {
  const ev = screen?.events.find((e) => e.name === eventName);
  return ev?.label ?? eventName;
}

/** Canvas shape of a journey edge; the handle carries the raw event name. */
export type JourneyEdgeView = {
  id: string;
  source: string;
  target: string;
  sourceHandle: string;
  label: string;
};

/**
 * RL-370 — the single place a journey edge gets its canvas label, so an edge
 * drawn in the editor and the same edge reloaded from the saved document read
 * identically (the manifest's event label, falling back to the event name).
 */
export function journeyEdgeView(
  edge: JourneyEdge,
  sourceScreen: ScreenNodeDef | undefined,
): JourneyEdgeView {
  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceEvent,
    label: eventLabel(sourceScreen, edge.sourceEvent),
  };
}

/**
 * Structural validation — kept in lock-step with the contract's
 * `validateJourneyGraph`. Returns [] when valid.
 */
export function validateJourneyGraph(graph: JourneyGraph, manifest: ScreenManifest): GraphIssue[] {
  const issues: GraphIssue[] = [];
  const byScreen = screenById(manifest);

  if (graph.manifestVersion !== manifest.manifestVersion) {
    issues.push({
      code: 'MANIFEST_VERSION_MISMATCH',
      message: `graph targets manifest ${graph.manifestVersion} but current is ${manifest.manifestVersion}`,
    });
  }

  const nodeById = new Map<string, JourneyNode>();
  for (const n of graph.nodes) {
    if (nodeById.has(n.nodeId))
      issues.push({ code: 'DUPLICATE_NODE', message: `duplicate node ${n.nodeId}` });
    nodeById.set(n.nodeId, n);
    if (!byScreen.has(n.screenId))
      issues.push({
        code: 'UNKNOWN_SCREEN',
        message: `node ${n.nodeId} → unknown screen ${n.screenId}`,
      });
  }

  const incoming = new Map<string, number>(graph.nodes.map((n) => [n.nodeId, 0]));
  const adjacency = new Map<string, string[]>();
  for (const e of graph.edges) {
    const src = nodeById.get(e.source);
    const tgt = nodeById.get(e.target);
    if (src === undefined || tgt === undefined) {
      issues.push({ code: 'DANGLING_EDGE', message: `edge ${e.id} references a missing node` });
      continue;
    }
    const srcScreen = byScreen.get(src.screenId);
    if (srcScreen !== undefined) {
      if (srcScreen.isTerminal)
        issues.push({
          code: 'TERMINAL_HAS_OUTGOING',
          message: `terminal ${src.screenId} cannot have outgoing edges`,
        });
      if (!srcScreen.events.some((ev) => ev.name === e.sourceEvent))
        issues.push({
          code: 'UNKNOWN_EVENT',
          message: `${src.screenId} does not emit "${e.sourceEvent}"`,
        });
    }
    incoming.set(e.target, (incoming.get(e.target) ?? 0) + 1);
    const list = adjacency.get(e.source) ?? [];
    list.push(e.target);
    adjacency.set(e.source, list);
  }

  if (graph.nodes.length > 0) {
    const entries = graph.nodes.filter((n) => (incoming.get(n.nodeId) ?? 0) === 0);
    if (entries.length === 0) {
      issues.push({ code: 'NO_ENTRY', message: 'no entry node (every node has an incoming edge)' });
    } else if (entries.length > 1) {
      issues.push({
        code: 'MULTIPLE_ENTRIES',
        message: `multiple entry nodes: ${entries.map((n) => n.nodeId).join(', ')}`,
      });
    } else {
      const entry = entries[0]!;
      const entryScreen = byScreen.get(entry.screenId);
      if (entryScreen !== undefined && !entryScreen.isStart)
        issues.push({
          code: 'ENTRY_NOT_START',
          message: `entry ${entry.nodeId} uses non-start screen ${entry.screenId}`,
        });
      const seen = new Set<string>([entry.nodeId]);
      const stack = [entry.nodeId];
      while (stack.length > 0) {
        const cur = stack.pop()!;
        for (const next of adjacency.get(cur) ?? []) {
          if (!seen.has(next)) {
            seen.add(next);
            stack.push(next);
          }
        }
      }
      for (const n of graph.nodes) {
        if (!seen.has(n.nodeId))
          issues.push({ code: 'UNREACHABLE', message: `node ${n.nodeId} unreachable from entry` });
      }
    }
  }

  return issues;
}
